import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from '@patternfly/react-core';
import { PlusCircleIcon, TrashIcon, CheckCircleIcon } from '@patternfly/react-icons';
import {
    PaymentMethodResponse,
    SetupIntentResponse
} from '@app/Subscription/types/subscription';
import {
    createPaymentMethodSetupIntent,
    getPaymentMethods,
    removePaymentMethod,
    setDefaultPaymentMethod
} from '@app/utils/utils';
import { AddPaymentMethodModal } from '@app/Subscription/AddPaymentMethodModal';

const styles: Record<string, React.CSSProperties> = {
    sectionCard: {
        background: '#ffffff',
        borderRadius: '14px',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    cardRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 0',
        borderBottom: '1px solid #f1f5f9'
    },
    cardBrand: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#1f2937',
        textTransform: 'capitalize'
    },
    cardMeta: { fontSize: '13px', color: '#6b7280' },
    defaultBadge: {
        background: '#dcfce7',
        color: '#166534',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
    },
    emptyState: {
        textAlign: 'center' as const,
        padding: '24px',
        color: '#6b7280',
        fontSize: '14px'
    },
    error: { color: '#b91c1c', fontSize: '13px', marginTop: '8px' }
};

export const PaymentMethodsSection: React.FC = () => {
    const [methods, setMethods] = useState<PaymentMethodResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [setupIntent, setSetupIntent] = useState<SetupIntentResponse | null>(null);
    const [openingAdd, setOpeningAdd] = useState(false);

    const reload = useCallback(async () => {
        try {
            const list = await getPaymentMethods();
            setMethods(list);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Failed to load cards');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const handleAddClick = async () => {
        setOpeningAdd(true);
        setError(null);
        try {
            const intent = await createPaymentMethodSetupIntent();
            setSetupIntent(intent);
        } catch (err: any) {
            setError(err.message || 'Failed to start adding a card');
        } finally {
            setOpeningAdd(false);
        }
    };

    const handleSetDefault = async (id: string) => {
        setBusyId(id);
        setError(null);
        try {
            await setDefaultPaymentMethod(id);
            await reload();
        } catch (err: any) {
            setError(err.message || 'Failed to set default');
        } finally {
            setBusyId(null);
        }
    };

    const handleRemove = async (id: string) => {
        setBusyId(id);
        setError(null);
        try {
            await removePaymentMethod(id);
            await reload();
        } catch (err: any) {
            setError(err.message || 'Failed to remove card');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div style={styles.sectionCard}>
            <div style={styles.sectionTitle}>
                <span>Payment methods</span>
                <Button
                    variant="link"
                    onClick={handleAddClick}
                    icon={<PlusCircleIcon />}
                    isDisabled={openingAdd}
                >
                    {openingAdd ? 'Opening…' : 'Add a card'}
                </Button>
            </div>

            {loading ? (
                <div style={styles.emptyState}>
                    <Spinner size="md" />
                </div>
            ) : methods.length === 0 ? (
                <div style={styles.emptyState}>No saved cards yet.</div>
            ) : (
                methods.map((pm) => (
                    <div key={pm.id} style={styles.cardRow}>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={styles.cardBrand}>
                                    {pm.brand || 'Card'} •••• {pm.last4 || '????'}
                                </span>
                                {pm.isDefault && (
                                    <span style={styles.defaultBadge}>
                                        <CheckCircleIcon /> Default
                                    </span>
                                )}
                            </div>
                            <div style={styles.cardMeta}>
                                Expires {String(pm.expMonth ?? '?').padStart(2, '0')}/
                                {pm.expYear ?? '????'}
                            </div>
                        </div>
                        {!pm.isDefault && (
                            <Button
                                variant="link"
                                onClick={() => handleSetDefault(pm.id)}
                                isDisabled={busyId === pm.id}
                            >
                                Make default
                            </Button>
                        )}
                        <Button
                            variant="plain"
                            aria-label="Remove card"
                            onClick={() => handleRemove(pm.id)}
                            isDisabled={busyId === pm.id}
                        >
                            <TrashIcon />
                        </Button>
                    </div>
                ))
            )}

            {error && <div style={styles.error}>{error}</div>}

            <AddPaymentMethodModal
                isOpen={!!setupIntent}
                setupIntent={setupIntent}
                onClose={() => setSetupIntent(null)}
                onSuccess={async (paymentMethodId) => {
                    setSetupIntent(null);
                    if (paymentMethodId) {
                        try {
                            await setDefaultPaymentMethod(paymentMethodId);
                        } catch {
                            // Card is saved; if defaulting fails, leave the existing default in place.
                        }
                    }
                    await reload();
                }}
            />
        </div>
    );
};
